import React from "react";
import { ShieldCheck, Check, X, Crown, Pencil, EyeOff, Network } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Drawer } from "../../components/Drawer";
import { Button, Badge, Avatar } from "../../components/primitives";
import {
  permissionFields, permissionOn, memberName, roleLabel,
  resolveReportingManagers, formatManagerName, memberLevel,
} from "./teamHelpers";

// Read-only view of what one member can actually do: their permission flags,
// who they report to, and where they sit in the org structure. Opened from the
// Permissions count on the team list.
//
// Read-only on purpose — the edit form already owns writing these, and two
// editable surfaces for the same flags drift apart.
export function MemberPermissionsPanel({ member, roles, allUsers, structure, showCrm, onEdit, onClose }) {
  const t = useDashTheme();

  const isAdmin = member?.role === "org_admin" || member?.role === "main_admin";
  const fields = permissionFields(showCrm);
  const granted = isAdmin ? fields.length : fields.filter((f) => permissionOn(member?.permissions, f.key)).length;
  const managers = member ? resolveReportingManagers(member, allUsers) : [];
  const level = member ? memberLevel(structure, member.position) : null;

  return (
    <Drawer
      open={!!member}
      onClose={onClose}
      title={member ? memberName(member) : "Permissions"}
      subtitle={member ? `${granted} of ${fields.length} permissions · ${roleLabel(roles, member.role)}` : ""}
      icon={<ShieldCheck size={18} />}
      width={620}
      footer={
        <>
          <Button variant="secondary" leftIcon={<Pencil size={14} />} onClick={onEdit}>Edit member</Button>
          <Button variant="primary" onClick={onClose}>Close</Button>
        </>
      }
    >
      {member && (
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[5] }}>
          {/* Who they are */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar user={member} name={memberName(member)} size={44} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>{memberName(member)}</div>
              <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                {member.position || "No position"}{level ? ` · Level ${level}` : ""}
              </div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Badge tone={isAdmin ? "warning" : "brand"}>{isAdmin && <Crown size={11} />}{roleLabel(roles, member.role)}</Badge>
              {member.stealth_mode && <Badge tone="info"><EyeOff size={11} /> Stealth</Badge>}
            </div>
          </div>

          {isAdmin && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: t.s[3], borderRadius: t.r.md, background: t.status.warningSoft, color: t.status.warning, fontSize: t.font.size.sm }}>
              <Crown size={14} /> Admins hold every permission implicitly. Per-permission flags only apply to members.
            </div>
          )}

          {/* Permission flags */}
          <Section t={t} title={`Permissions (${granted}/${fields.length})`}>
            {fields.map((f) => {
              const on = isAdmin || permissionOn(member.permissions, f.key);
              return (
                <div key={f.key} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: `${t.s[3]}px 0`,
                  borderBottom: `1px solid ${t.c.border}`,
                }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    background: on ? t.status.positiveSoft : t.c.surfaceMuted,
                    color: on ? t.status.positive : t.c.textTertiary,
                  }}>{on ? <Check size={13} /> : <X size={13} />}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: t.font.size.md, color: on ? t.c.text : t.c.textSecondary, fontWeight: on ? 500 : 400 }}>{f.label}</div>
                    <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{f.desc}</div>
                  </div>
                  <span style={{ fontSize: t.font.size.xs, fontWeight: 600, color: on ? t.status.positive : t.c.textTertiary }}>
                    {on ? "ON" : "OFF"}
                  </span>
                </div>
              );
            })}
          </Section>

          {/* Reporting chain */}
          <Section t={t} title={`Reports to (${managers.length})`}>
            {managers.length === 0 ? (
              <div style={{ fontSize: t.font.size.md, color: t.c.textTertiary, paddingTop: t.s[2] }}>
                No reporting manager assigned.
              </div>
            ) : managers.map((m, i) => (
              <div key={m._id || m.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: `${t.s[3]}px 0`, borderBottom: `1px solid ${t.c.border}` }}>
                <Avatar user={m} name={formatManagerName(m)} size={28} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.md, color: t.c.text }}>{formatManagerName(m)}</div>
                  <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{m.position || m.email || "—"}</div>
                </div>
              </div>
            ))}
          </Section>

          {/* What the structure implies — the non-obvious part of permissions */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: t.s[3], borderRadius: t.r.md, background: t.c.surfaceMuted, fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5 }}>
            <Network size={14} style={{ flexShrink: 0, marginTop: 2, color: t.c.textTertiary }} />
            <span>
              {isAdmin
                ? "As an admin, this member can see every member in the organization."
                : level
                  ? `At level ${level}, this member can only see themselves and members at deeper levels of the org structure.`
                  : "This member has no position mapped to the org structure, so they can only see themselves."}
            </span>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function Section({ t, title, children }) {
  return (
    <div>
      <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
        {title}
      </div>
      <div style={{ marginTop: t.s[2] }}>{children}</div>
    </div>
  );
}
